import React, { useState } from 'react'
import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import TableBody from '@mui/material/TableBody'
import IconButton from '@mui/material/IconButton'
import MenuItem from '@mui/material/MenuItem'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import DoneIcon from '@mui/icons-material/Done'
import CloseIcon from '@mui/icons-material/Close'
import { useTranslation } from 'react-i18next'
import CardContentText from 'Components/reusable/CaleoCustomComponents/CardContentText'
import SkillLevelChip from 'Components/reusable/CaleoCustomComponents/SkillLevelChip'
import { InputField } from 'Components/reusable/InputFields/InputField'
import { IParsedProfileNamed } from 'types/cvParserInterfaces'
import { formatParsedExperience, parsedName } from 'utils/parsedProfileFields'
import ExperienceInputs from './ExperienceInputs'

/** @notExported */
interface IParsedNamesTableProps {
  /** Section header, e.g. "Roles" */
  title: string
  /** Roles or industries on the profile */
  items: IParsedProfileNamed[]
  /** Called with the edited roles or industries */
  onChange: (items: IParsedProfileNamed[]) => void
  /** Language the names are shown and edited in */
  languageId: number
  /** Show without actions (the CV is already saved) */
  readOnly?: boolean
  /** Prefix of test ids, e.g. "roles" */
  testIdPrefix: string
  /** Shown after an item's name, e.g. whether saving would create a new option for it */
  renderStatus?: (item: IParsedProfileNamed) => React.ReactNode
}

/**
 * Parsed roles or industries of the profile, with the experience the CV states for them
 *
 * @returns The table
 * @notExported
 */
const ParsedNamesTable: React.FC<IParsedNamesTableProps> = ({
  title,
  items,
  onChange,
  languageId,
  readOnly = false,
  testIdPrefix,
  renderStatus,
}) => {
  const { t } = useTranslation()
  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const [temp, setTemp] = useState<IParsedProfileNamed | null>(null)

  const setName = (item: IParsedProfileNamed, name: string): IParsedProfileNamed => ({
    ...item,
    translations: item.translations.some(tr => tr.languageId === languageId)
      ? item.translations.map(tr => (tr.languageId === languageId ? { ...tr, name } : tr))
      : [...item.translations, { languageId, name }],
  })

  return (
    <>
      <CardContentText type="darkSubHeader">{title}</CardContentText>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>{t('admin.parse.result.name')}</TableCell>
            <TableCell>{t('admin.parse.result.level')}</TableCell>
            <TableCell>{t('admin.parse.result.experienceMonths')}</TableCell>
            {!readOnly && <TableCell width={100}>{t('admin.parse.result.actions')}</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item, index) => {
            const isEditing = editingIndex === index && temp !== null
            const nameMissing = isEditing && !parsedName(temp, languageId).trim()
            return (
              <TableRow key={index}>
                <TableCell>
                  {isEditing ? (
                    <InputField
                      value={temp.translations.find(tr => tr.languageId === languageId)?.name ?? ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTemp(setName(temp, e.target.value))}
                      error={nameMissing && t('fieldRequired')}
                      fullWidth
                      data-testid={`field-admin-parse-${testIdPrefix}-name-${index}`}
                    />
                  ) : (
                    <>
                      {parsedName(item, languageId)}
                      {renderStatus?.(item)}
                    </>
                  )}
                </TableCell>
                <TableCell>
                  {isEditing ? (
                    <InputField
                      select
                      value={temp.level ?? 0}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setTemp({ ...temp, level: Number(e.target.value) || null })
                      }
                      fullWidth
                      data-testid={`field-admin-parse-${testIdPrefix}-level-${index}`}
                    >
                      <MenuItem value="1">{t('profile.skill.fundamental')}</MenuItem>
                      <MenuItem value="2">{t('profile.skill.novice')}</MenuItem>
                      <MenuItem value="3">{t('profile.skill.intermediate')}</MenuItem>
                      <MenuItem value="4">{t('profile.skill.advanced')}</MenuItem>
                      <MenuItem value="5">{t('profile.skill.expert')}</MenuItem>
                    </InputField>
                  ) : (
                    <SkillLevelChip level={item.level ?? 0} type="skill" />
                  )}
                </TableCell>
                <TableCell>
                  {isEditing ? (
                    <ExperienceInputs
                      value={temp}
                      onChange={experience => setTemp({ ...temp, ...experience })}
                      testIdPrefix={`field-admin-parse-${testIdPrefix}-${index}`}
                    />
                  ) : (
                    formatParsedExperience(item, t)
                  )}
                </TableCell>
                {!readOnly && (
                  <TableCell>
                    {isEditing ? (
                      <>
                        <IconButton
                          data-testid={`button-admin-parse-${testIdPrefix}-save-${index}`}
                          size="small"
                          color="primary"
                          disabled={nameMissing}
                          onClick={() => {
                            onChange(items.map((other, i) => (i === index ? temp : other)))
                            setEditingIndex(null)
                          }}
                        >
                          <DoneIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          data-testid={`button-admin-parse-${testIdPrefix}-cancel-${index}`}
                          size="small"
                          onClick={() => setEditingIndex(null)}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </>
                    ) : (
                      <>
                        <IconButton
                          data-testid={`button-admin-parse-${testIdPrefix}-edit-${index}`}
                          size="small"
                          onClick={() => {
                            setEditingIndex(index)
                            setTemp(item)
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          data-testid={`button-admin-parse-${testIdPrefix}-delete-${index}`}
                          size="small"
                          onClick={() => onChange(items.filter((_, i) => i !== index))}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </>
                    )}
                  </TableCell>
                )}
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </>
  )
}

export default ParsedNamesTable
